import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useFetchData from '../../hooks/useFetchData'
import { ChartBarIcon, HomeModernIcon, InformationCircleIcon, PencilIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Fade } from 'react-awesome-reveal'
import { useAuth } from '../../context/AuthContext'
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    filterFns,
} from '@tanstack/react-table'
import { formatDate } from '../../utils/format.utils'
import sortingUtils from '../../utils/sorting.utils'
import LoadingSpinner from '../Common/Loading/LoadingSpinner'
import { UserIcon } from '@heroicons/react/24/outline'
import ReagentsModal from './ReagentsModal'
import { ArrowLeft, ArrowRight } from 'lucide-react'

const CapsuleReview = () => {
    const { user } = useAuth()
    const [searchParam] = useSearchParams()
    const token = searchParam.get('token')
    const { data, error, isLoading } = useFetchData(`/capsules/resume/${token}`)
    const [activeTab, setActiveTab] = useState('sensitivity')
    const [editMode, setEditMode] = useState(false)
    const [sorting, setSorting] = useState([])
    const [activeCapsuleIndex, setActiveCapsuleIndex] = useState(0)
    const [processedCapsules, setProcessedCapsules] = useState([])

    // Process capsules data when it's available
    useEffect(() => {
        if (!data || !data.capsules) return;

        const capsules = data.capsules.map(capsule => {
            // Sort configurations by creation date to get the latest
            const sortedConfigs = [...capsule.configurations].sort(
                (a, b) => new Date(a.creationDate) - new Date(b.creationDate)
            );

            const latestConfig = sortedConfigs[sortedConfigs.length - 1];
            const firstConfig = sortedConfigs[0];
            return {
                ...capsule,
                latestConfiguration: latestConfig,
                firstConfiguration: firstConfig,
                hasMultipleConfigs: sortedConfigs.length > 1
            };
        });

        setProcessedCapsules(capsules);
    }, [data]);

    const getCapsuleData = () => {
        if (processedCapsules.length === 0) return [];
        const activeCapsule = processedCapsules[activeCapsuleIndex];
        const data = activeCapsule?.latestConfiguration?.configuration?.content || [];
        
        // Trier les données par category en mode ascendant
        return data.sort((a, b) => {
            const categoryA = a.mixture.category || '';
            const categoryB = b.mixture.category || '';
            return categoryB.localeCompare(categoryA);
        });
    }

    const getFirstConfigSensibility = (mixtureId) => {
        const activeCapsule = processedCapsules[activeCapsuleIndex];
        if (!activeCapsule?.firstConfiguration?.configuration?.content) return '-';

        const firstConfigRow = activeCapsule.firstConfiguration.configuration.content.find(
            row => row.mixture.id === mixtureId
        );

        return firstConfigRow && firstConfigRow.sensibility !== null ? firstConfigRow.sensibility : '-';
    }

    const columns = [
        {
            accessorKey: 'mixture.name',
            header: 'Mélange',
            cell: ({ row }) => {
                const isEffluence = row.original.mixture.category === 'Effluences';
                return (
                    <span className={isEffluence ? 'text-accent' : ''}>
                        {row.original.mixture.name}
                    </span>
                );
            },
        },
        {
            id: 'sensibilityInitial',
            accessorFn: row => getFirstConfigSensibility(row.mixture.id),
            header: 'Sensibilité',
            cell: info => {
                const value = info.getValue();
                const className = value !== '-' ? 'badge badge-sm badge-ghost' : '';
                return <span className={className}>{value}</span>;
            },
            footer: props => props.column.id,
            size: 125
        },
        {
            id: 'sensibility',
            accessorKey: 'sensibility',
            header: 'Validation',
            cell: ({ row }) => {
                const activeCapsule = processedCapsules[activeCapsuleIndex];
                // If there's only one configuration, show "-"
                if (!activeCapsule?.hasMultipleConfigs) {
                    return '-';
                }
                const className = row.original.sensibility === null ? '' : 'badge badge-sm badge-primary';
                return <span className={className}>{row.original.sensibility === null ? '-' : row.original.sensibility}</span>;
            },
        },
        {
            accessorKey: 'dilution',
            header: 'Dilution',
            cell: ({ row }) => row.original.dilution !== null ? row.original.dilution.label : row.original.mixture.defaultDilution.label,
        },
        {
            accessorKey: 'mixture.subCategory',
            header: 'Catégorie',
            cell: ({ row }) => row.original.mixture.subCategory,
        },
        {
            accessorKey: 'mixture.reagents',
            header: 'Réactifs',
            cell: ({ row }) => <ReagentsModal mixtureName={row.original.mixture.name} reagents={row.original.mixture.reagents} description={row.original.mixture.description} />,
        },
        {
            accessorKey: 'actions',
            header: 'Infos',
            cell: ({ row }) => <ReagentsModal mixtureName={row.original.mixture.name} reagents={row.original.mixture.reagents} description={row.original.mixture.description} showPreview={false} />,
        }
    ]

    const table = useReactTable({
        data: getCapsuleData(),
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    const handleCapsuleChange = (index) => {
        setActiveCapsuleIndex(index);
    }

    if (isLoading) return <LoadingSpinner />
    if (error && error?.response?.data?.message) return <div className="p-4 text-center text-red-600">{error?.response?.data?.message || 'Error'}</div>

    if (!data || processedCapsules.length === 0) return <div className="p-4 text-center">Aucune donnée disponible</div>;

    return (
        <div className="container mx-auto px-2 py-3  lg:px-4 lg:py-6 max-w-7xl">
            {/* Header Section - Improved layout for desktop */}
            <div className="p-2 lg:p-6 mb-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-8">
                    {/* Customer and Therapist Info */}
                    <div className="flex flex-col justify-center">
                        <h1 className="text-2xl md:text-3xl font-bold  mb-2 rounded-2xl shadow-md py-1 px-6 border border-primary/50 w-fit text-base-content">
                            {data.customer.firstname} {data.customer.lastname}
                        </h1>


                        {/* Therapist Info */}
                        <div className="flex flex-col items-start  rounded-2xl shadow-md py-1 px-6 border border-primary/50 w-fit text-base-content">
                            <h2 className="text-xl font-semibold text-base-content mb-3">Votre thérapeute</h2>
                            <div className="flex items-center mb-3">
                                <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mr-3">
                                    <UserIcon className="h-8 w-8 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-lg">
                                        {data.therapist.firstname} {data.therapist.lastname}
                                    </h3>
                                    <p className="text-base-content text-sm">{data.therapist.email}</p>
                                    {data.therapist.phone && (
                                        <p className="text-base-content text-sm">{data.therapist.phone}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Understanding Panel */}
                    <div className="flex flex-col justify-center  rounded-2xl shadow-md pt-2 pb-3 px-6 border border-primary/50 w-fit text-base-content">
                        <div className="flex items-center mb-4">
                            <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mr-3">
                                <InformationCircleIcon className="h-6 w-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold text-base-content">Comprendre ma consultation</h3>
                        </div>

                        <div className="mb-4">
                            <div className="flex justify-center">
                                <button
                                    className={`pb-3 px-4 text-base font-medium border-b-2 transition-colors ${activeTab === 'sensitivity'
                                        ? 'text-primary border-primary'
                                        : 'text-base-content/70 border-transparent hover:text-base-content'
                                        }`}
                                    onClick={() => setActiveTab('sensitivity')}
                                >
                                    Sensibilité
                                </button>
                                <button
                                    className={`pb-3 px-4 text-base font-medium border-b-2 transition-colors ${activeTab === 'dilution'
                                        ? 'text-primary border-primary'
                                        : 'text-base-content/70 border-transparent hover:text-base-content'
                                        }`}
                                    onClick={() => setActiveTab('dilution')}
                                >
                                    Dilution
                                </button>
                            </div>
                        </div>

                        <div className="overflow-auto max-h-72">
                            {activeTab === 'sensitivity' && (
                                <Fade>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [0] Non sensible
                                            </span>
                                            <p className="text-sm text-base-content">Aucune réaction</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [1] Sensible
                                            </span>
                                            <p className="text-sm text-base-content">Réaction légère</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [2] Hypersensible
                                            </span>
                                            <p className="text-sm text-base-content">Réaction modérée</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [3] Intolérant
                                            </span>
                                            <p className="text-sm text-base-content">Réaction importante</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [4] Allergique
                                            </span>
                                            <p className="text-sm text-base-content">Réaction allergique</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [5] Anaphylactique
                                            </span>
                                            <p className="text-sm text-base-content">Réaction sévère</p>
                                        </div>
                                    </div>
                                </Fade>
                            )}
                            {activeTab === 'dilution' && (
                                <Fade>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [A]tténuer
                                            </span>
                                            <p className="text-sm text-base-content">Pour les réactifs pathogènes</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [R]elancer
                                            </span>
                                            <p className="text-sm text-base-content">Pour les réactifs organiques</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [S]yntoniser
                                            </span>
                                            <p className="text-sm text-base-content">Allergie au sens propre</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [E]ffluences
                                            </span>
                                            <p className="text-sm text-base-content">Soutien émotionnel</p>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="px-3 py-1 bg-base-200 rounded text-primary mr-2 text-sm font-medium min-w-max">
                                                [T]ransmuter
                                            </span>
                                            <p className="text-sm text-base-content">Reconstruction émotionnelle</p>
                                        </div>
                                    </div>
                                </Fade>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Capsules Section */}
            {processedCapsules.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm lg:p-6 mb-6">
                    {/* Capsule Selection */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                        <div className="flex gap-2 overflow-x-auto">
                            {processedCapsules.map((capsule, index) => (
                                <button
                                    key={capsule.id}
                                    onClick={() => handleCapsuleChange(index)}
                                    className={`px-4 py-2 rounded-md font-medium whitespace-nowrap ${index === activeCapsuleIndex
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        } transition-colors`}
                                >
                                    Capsule {capsule.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-base-300">
                            <thead className="bg-base-300">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {header.column.getCanSort() && (
                                                        <>
                                                            {header.column.getIsSorted() === 'asc' && (
                                                                <ChevronUpIcon className="h-3 w-3 text-primary" />
                                                            )}
                                                            {header.column.getIsSorted() === 'desc' && (
                                                                <ChevronDownIcon className="h-3 w-3 text-primary" />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="sm:hidden flex justify-center flex-col items-center gap-2">
                <p className='text-xs text-gray-500'>Vous pouvez faire défiler le tableau horizontalement</p>
                <div className="flex flex-row gap-2">
                    <ArrowLeft className="w-3 h-3" /><ArrowRight className="w-3 h-3" />
                </div>
            </div>
        </div>
    )
}

export default CapsuleReview
